<template>
  <div class="menu-flow-designer" :class="{'viewer-mode': viewer}">
    <MenuLibraryPanel v-if="lf && !viewer" :lf="lf" />
    <div :id="id" class="lf-container" :class="{'with-panel': !viewer}"></div>
    <PropertySetting 
      ref="propertySetting" 
      :node="nodeClick" 
      v-model="flowData" 
      :lf="lf"
    />
  </div>
</template>

<script>
import LogicFlow from '@logicflow/core'
import MenuTask from './flownode/MenuTask'
import FlowTransition from './flownode/FlowTransition'
import PropertySetting from './PropertySetting/index.vue'
import MenuLibraryPanel from './MenuLibraryPanel.vue'

import { Snapshot, SelectionSelect, Menu, Control } from '@logicflow/extension'
import '@logicflow/core/dist/style/index.css'
import '@logicflow/extension/lib/style/index.css'

export default {
  name: 'FlowDesigner',
  components: {
    PropertySetting,
    MenuLibraryPanel
  },
  props: {
    id: {
      type: String,
      default() {
        return 'flow_' + Date.now()
      }
    },
    value: {
      type: Object,
      default() {
        return undefined
      }
    },
    config: {
      type: Object,
      default() {
        return {
          grid: false,
          background: {
            backgroundColor: '#ffffff'
          }
        }
      }
    },
    viewer: {
      type: Boolean,
      default: false
    },
    highLight: {
      type: Object,
      default() {
        return {}
      }
    },
    // 用户可访问的路由列表
    allowedRoutes: {
      type: Array,
      default() {
        return []
      }
    }
  },
  data() {
    return {
      lf: null,
      nodeClick: null,
      flowData: {},
      viewerMouseDownHandler: null,
      viewerMouseMoveHandler: null,
      viewerMouseUpHandler: null,
      viewerDownOnNode: false,
      viewerMovedOnNode: false
    }
  },
  watch: {
    value: {
      handler(n) {
        if (n && this.lf) {
          if (n.nodes) {
            this.lf.render(n)
            // 在查看模式下，根据权限设置节点状态
            if (this.viewer) {
              this.updateNodePermissions()
            }
          }
        }
      },
      deep: true
    },
    flowData: {
      handler(n) {
        if (n) {
          this.$emit('input', n)
        }
      },
      deep: true
    },
    highLight: {
      handler(n) {
        this.setHighLight(n)
      },
      deep: true
    },
    allowedRoutes: {
      handler() {
        // 权限列表变化时，更新节点状态
        if (this.viewer && this.lf) {
          this.updateNodePermissions()
        }
      },
      deep: true
    }
  },
  mounted() {
    this.init()
    if (this.value) {
      this.lf.render(this.value)
      this.setHighLight(this.highLight)
    }
  },
  beforeDestroy() {
    console.log('FlowDesigner 组件销毁')
    
    // 移除事件监听
    const container = document.getElementById(this.id)
    if (container) {
      container.removeEventListener('dragover', this.handleDragOver)
      container.removeEventListener('drop', this.handleDrop)
      
      // 移除查看模式事件监听
      if (this.viewer) {
        container.removeEventListener('wheel', this.preventZoom)
        if (this.viewerMouseDownHandler) {
          container.removeEventListener('mousedown', this.viewerMouseDownHandler, true)
        }
        if (this.viewerMouseMoveHandler) {
          container.removeEventListener('mousemove', this.viewerMouseMoveHandler, true)
        }
        if (this.viewerMouseUpHandler) {
          container.removeEventListener('mouseup', this.viewerMouseUpHandler, true)
        }
      }
    }
    
    // 移除 LogicFlow 事件监听
    if (this.lf) {
      this.lf.off('node:click')
      this.lf.off('edge:click')
      this.lf.off('edge:add')
      this.lf.off('blank:click')
      
      // 销毁 LogicFlow 实例
      if (typeof this.lf.destroy === 'function') {
        this.lf.destroy()
      }
      
      this.lf = null
    }
    
    // 清空数据
    this.flowData = {}
    this.nodeClick = null
  },
  methods: {
    init() {
      LogicFlow.use(Snapshot)
      LogicFlow.use(SelectionSelect)
      LogicFlow.use(Menu)
      
      // 只在非查看模式下加载控制面板
      if (!this.viewer) {
        LogicFlow.use(Control)
      }
      
      const defaultConfig = {}
      if (this.viewer) {
        defaultConfig.adjustEdge = false
        defaultConfig.adjustNodePosition = false
        defaultConfig.stopScrollGraph = true
        defaultConfig.stopZoomGraph = true  // 禁止缩放
        defaultConfig.hoverOutline = false
        defaultConfig.hideAnchors = true
        defaultConfig.nodeTextEdit = false
        defaultConfig.edgeTextEdit = false
        defaultConfig.resizeNode = false
        defaultConfig.nodeSelectedOutline = false
      }
      
      const lf = new LogicFlow({
        ...this.config,
        ...defaultConfig,
        container: document.getElementById(this.id)
      })
      
      this.lf = lf
      
      // 在 viewer 模式下，手动禁用滚轮缩放和画布拖动
      if (this.viewer) {
        const container = document.getElementById(this.id)
        if (container) {
          container.addEventListener('wheel', this.preventZoom, { passive: false })
          this.bindViewerDragBlock(container)
        }
      }
      
      // 注册自定义元素
      this.lf.register(MenuTask)
      this.lf.register(FlowTransition)
      
      // 初始化操作栏和事件
      this.initOp(this.lf)
      this.initEvent(lf)
    },
    
    preventZoom(event) {
      // 阻止滚轮缩放
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault()
        event.stopPropagation()
        return false
      }
    },

    bindViewerDragBlock(container) {
      this.viewerMouseDownHandler = (event) => {
        const isNode = !!event.target.closest('.lf-node')
        const isEdge = !!event.target.closest('.lf-edge')

        this.viewerDownOnNode = isNode
        this.viewerMovedOnNode = false

        // 连线保留默认事件，确保点击事件正常触发
        if (isEdge) {
          return
        }

        // 节点按下时不拦截，保留 click；拖动阶段再阻止画布跟随移动
        if (isNode) {
          return
        }

        // 空白区域直接阻止，禁止拖动画布
        event.preventDefault()
        event.stopPropagation()
      }

      this.viewerMouseMoveHandler = (event) => {
        if (!this.viewerDownOnNode) {
          return
        }

        this.viewerMovedOnNode = true
        event.preventDefault()
        event.stopPropagation()
      }

      this.viewerMouseUpHandler = () => {
        this.viewerDownOnNode = false
        this.viewerMovedOnNode = false
      }

      container.addEventListener('mousedown', this.viewerMouseDownHandler, true)
      container.addEventListener('mousemove', this.viewerMouseMoveHandler, true)
      container.addEventListener('mouseup', this.viewerMouseUpHandler, true)
    },
    
    initOp(lf) {
      if (this.viewer) {
        this.lf.extension.menu.setMenuConfig({
          nodeMenu: [],
          edgeMenu: []
        })
        return
      }
      
      // 监听画布的拖拽事件
      const container = document.getElementById(this.id)
      if (container) {
        container.addEventListener('dragover', this.handleDragOver)
        container.addEventListener('drop', this.handleDrop)
      }
      
      // 设置右键菜单
      this.lf.extension.menu.setMenuConfig({
        nodeMenu: [
          {
            text: '删除',
            callback(node) {
              lf.deleteNode(node.id)
            }
          },
          {
            text: '复制',
            callback(node) {
              lf.cloneNode(node.id)
            }
          }
        ],
        edgeMenu: [
          {
            text: '删除',
            callback(edge) {
              lf.deleteEdge(edge.id)
            }
          }
        ]
      })
      
      // 控制面板-清空画布
      this.lf.extension.control.addItem({
        iconClass: 'lf-control-clear',
        title: 'clear',
        text: '清空',
        onClick: (lf, ev) => {
          this.$confirm('确定要清空画布吗？', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }).then(() => {
            lf.clearData()
          }).catch(() => {})
        }
      })
      
      // 控制面板-保存
      this.lf.extension.control.addItem({
        iconClass: 'lf-control-save',
        title: '',
        text: '保存',
        onClick: (lf, ev) => {
          const graphData = lf.getGraphData()
          this.$emit('on-save', graphData)
        }
      })
    },
    
    initEvent(lf) {
      const { eventCenter } = this.lf.graphModel
      
      if (this.viewer) {
        // 查看模式：点击节点跳转到对应菜单
        eventCenter.on('node:click', (args) => {
          const node = args.data
          const props = node.properties || {}
          
          // 检查是否有权限
          if (props.disabled === true || props.hasPermission === false) {
            this.$message.warning('您没有访问该菜单的权限')
            return
          }
          
          // 只处理表单节点（有 menuUrl 的节点）
          if (props.menuUrl && props.menuUrl.trim()) {
            // 触发跳转事件，让父组件处理
            this.$emit('node-click', {
              menuUrl: props.menuUrl,
              menuCode: props.menuCode,
              menuName: node.text instanceof Object ? node.text.value : node.text,
              nodeData: node
            })
          } else {
            // 模块节点没有 URL
            this.$message.info('该节点没有配置菜单路径')
          }
        })
        return
      }
      
      // 编辑模式：打开属性面板
      eventCenter.on('node:click', (args) => {
        this.nodeClick = args.data
        this.$nextTick(() => {
          this.$refs.propertySetting.show(args.data)
        })
      })
      
      eventCenter.on('edge:click', (args) => {
        this.nodeClick = args.data
        this.$nextTick(() => {
          this.$refs.propertySetting.show(args.data)
        })
      })
      
      eventCenter.on('edge:add', (args) => {
        lf.changeEdgeType(args.data.id, 'flow:transition')
      })
      
      eventCenter.on('blank:click', () => {
        this.$refs.propertySetting.handleClose()
      })
    },
    
    handleDragOver(event) {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'copy'
    },
    
    handleDrop(event) {
      event.preventDefault()
      
      const dragDataStr = event.dataTransfer.getData('application/json')
      if (!dragDataStr) return
      
      const dragData = JSON.parse(dragDataStr)
      const point = this.getDropPoint(event)
      
      if (!point) return
      
      if (dragData.type === 'moduleWithForms') {
        this.createModuleWithForms(point, dragData.module)
      } else if (dragData.type === 'single') {
        this.createMenuNode(point, dragData.form, dragData.module)
      }
    },
    
    getDropPoint(event) {
      let point = this.lf.getPointByClient(event.clientX, event.clientY)
      
      if (!point || isNaN(point.x) || isNaN(point.y) || Math.abs(point.y) > 10000) {
        const container = document.getElementById(this.id)
        const rect = container.getBoundingClientRect()
        const domX = event.clientX - rect.left
        const domY = event.clientY - rect.top
        const graphModel = this.lf.graphModel
        const transformModel = graphModel.transformModel
        
        point = {
          x: (domX - transformModel.TRANSLATE_X) / transformModel.SCALE_X,
          y: (domY - transformModel.TRANSLATE_Y) / transformModel.SCALE_Y
        }
      }
      
      if (Math.abs(point.x) > 10000 || Math.abs(point.y) > 10000) {
        point = { x: 0, y: 0 }
      }
      
      return point
    },
    
    createMenuNode(point, form, module) {
      const nodeId = `menu_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      
      const nodeData = {
        id: nodeId,
        type: 'flow:menutask',
        x: point.x,
        y: point.y,
        text: form.FRMNM,
        properties: {
          nodeType: 'form',
          displayName: form.FRMNM,       // 菜单名称
          menuCode: form.IDSEQ,          // 菜单编码 (IDSEQ)
          menuUrl: form.FRMURL || '',    // 菜单URL (FRMURL)
          menuIcon: 'el-icon-s-grid',    // 默认图标
          formCode: form.FRMIDSEQ,       // 表单主键
          formName: form.FRMNM,          // 表单名称
          moduleCode: module.IDSEQ || module.MODIDSEQ,  // 模块主键
          moduleName: module.MODNM,      // 模块名称
          width: 140,
          height: 60,
          sortOrder: form.SORTBY || 0,
          visible: true,
          permissions: [],
          roles: []
        }
      }
            
      this.lf.addNode(nodeData)
    },
    
    createModuleWithForms(point, module) {
      const baseTime = Date.now()
      const spacing = 100
      const horizontalSpacing = 200
      
      // 创建模块节点（在左边）
      const moduleNodeId = `module_${baseTime}_${Math.random().toString(36).substr(2, 9)}`
      const moduleNode = {
        id: moduleNodeId,
        type: 'flow:menutask',
        x: point.x,
        y: point.y,
        text: module.MODNM,
        properties: {
          nodeType: 'module',
          displayName: module.MODNM,     // 菜单名称
          menuCode: module.IDSEQ || module.MODIDSEQ,  // 菜单编码（模块主键）
          menuUrl: '',                   // 模块没有URL
          menuIcon: 'el-icon-menu',      // 默认图标
          moduleCode: module.IDSEQ || module.MODIDSEQ,  // 模块主键
          moduleName: module.MODNM,      // 模块名称
          width: 140,
          height: 60,
          sortOrder: 0,
          visible: true,
          permissions: [],
          roles: []
        }
      }
            
      this.lf.addNode(moduleNode)
      
      // 创建表单节点，在右边垂直排列
      const forms = module.forms || []
      forms.forEach((form, index) => {
        const formNodeId = `form_${baseTime + index + 1}_${Math.random().toString(36).substr(2, 9)}`
        const formY = point.y + (index * spacing)
        
        const formNode = {
          id: formNodeId,
          type: 'flow:menutask',
          x: point.x + horizontalSpacing,
          y: formY,
          text: form.FRMNM,
          properties: {
            nodeType: 'form',
            displayName: form.FRMNM,       // 菜单名称
            menuCode: form.IDSEQ,          // 菜单编码 (IDSEQ)
            menuUrl: form.FRMURL || '',    // 菜单URL (FRMURL)
            menuIcon: 'el-icon-s-grid',    // 默认图标
            formCode: form.FRMIDSEQ,       // 表单主键
            formName: form.FRMNM,          // 表单名称
            moduleCode: module.IDSEQ || module.MODIDSEQ,  // 模块主键
            moduleName: module.MODNM,      // 模块名称
            width: 140,
            height: 60,
            sortOrder: form.SORTBY || index,
            visible: true,
            permissions: [],
            roles: []
          }
        }
        
        this.lf.addNode(formNode)
      })
    },
    
    setHighLight(data) {
      if (!this.lf || !data) return
      
      if (data.historyNodeIds) {
        data.historyNodeIds.forEach(nodeId => {
          this.lf.setProperties(nodeId, { state: 'history' })
        })
      }
      
      if (data.activeNodeIds) {
        data.activeNodeIds.forEach(nodeId => {
          this.lf.setProperties(nodeId, { state: 'active' })
        })
      }
      
      if (data.historyEdgeIds) {
        data.historyEdgeIds.forEach(edgeId => {
          const edgeModel = this.lf.getEdgeModelById(edgeId)
          if (edgeModel) {
            edgeModel.setProperties({ state: 'history' })
          }
        })
      }
    },
    
    // 导出数据
    exportData() {
      return this.lf.getGraphData()
    },
    
    // 导入数据
    importData(data) {
      this.lf.render(data)
      // 在查看模式下，根据权限设置节点状态
      if (this.viewer) {
        this.$nextTick(() => {
          this.updateNodePermissions()
        })
      }
    },
    
    // 检查路由是否在允许列表中
    isRouteAllowed(menuUrl) {
      if (!menuUrl || !menuUrl.trim()) {
        return false // 没有 URL 的节点（模块节点）不可点击
      }
      
      if (!this.allowedRoutes || this.allowedRoutes.length === 0) {
        return false // 如果没有提供权限列表，默认全部可访问
      }
      
      // 递归检查路由权限
      const checkRoute = (routes) => {
        for (const route of routes) {
          // 检查当前路由
          if (route.frmurl === menuUrl) {
            return true
          }
          // 检查子路由
          if (route.children && route.children.length > 0) {
            if (checkRoute(route.children)) {
              return true
            }
          }
        }
        return false
      }
      
      return checkRoute(this.allowedRoutes)
    },
    
    // 根据权限更新节点状态
    updateNodePermissions() {
      if (!this.lf || !this.viewer) return
      
      const graphData = this.lf.getGraphData()
      
      graphData.nodes.forEach(node => {
        const props = node.properties || {}
        const menuUrl = props.menuUrl
        
        const hasPermission = this.isRouteAllowed(menuUrl)
        
        // 设置节点的权限状态
        this.lf.setProperties(node.id, {
          ...props,
          hasPermission: hasPermission,
          disabled: !hasPermission
        })
      })
    }
  }
}
</script>

<style scoped>
.menu-flow-designer {
  height: 100%;
  position: relative;
  display: flex;
}

.lf-container {
  flex: 1;
  height: 100%;
}

.lf-container.with-panel {
  margin-left: 280px;
}

/* 查看模式下节点显示手型光标 */
.menu-flow-designer ::v-deep .lf-node {
  cursor: default;
}

.menu-flow-designer.viewer-mode ::v-deep .lf-node {
  cursor: pointer !important;
}

/* 查看模式下节点文字也显示手型光标 - 使用 !important 强制覆盖 */
.menu-flow-designer.viewer-mode ::v-deep .lf-node-text {
  cursor: pointer !important;
}

.menu-flow-designer.viewer-mode ::v-deep .lf-node-text text {
  cursor: pointer !important;
  pointer-events: auto;
}

/* 查看模式下节点文字也显示手型光标 - HTML div */
.menu-flow-designer.viewer-mode ::v-deep .lf-node-text div {
  cursor: pointer !important;
  pointer-events: auto;
}

.menu-flow-designer.viewer-mode ::v-deep .lf-node-text-content {
  cursor: pointer !important;
  pointer-events: auto;
}

/* 所有可能的文字容器 */
.menu-flow-designer.viewer-mode ::v-deep .lf-node foreignObject {
  cursor: pointer !important;
  pointer-events: auto;
}

.menu-flow-designer.viewer-mode ::v-deep .lf-node foreignObject div {
  cursor: pointer !important;
  pointer-events: auto;
}

.menu-flow-designer.viewer-mode ::v-deep .lf-node foreignObject * {
  cursor: pointer !important;
}

/* 覆盖所有节点内的元素 */
.menu-flow-designer.viewer-mode ::v-deep .lf-node * {
  cursor: pointer !important;
}

/* 禁用状态的节点显示禁止光标 */
.menu-flow-designer.viewer-mode ::v-deep .lf-node[data-disabled="true"] {
  cursor: not-allowed !important;
}

.menu-flow-designer.viewer-mode ::v-deep .lf-node[data-disabled="true"] * {
  cursor: not-allowed !important;
}

.menu-flow-designer.viewer-mode ::v-deep .lf-node[data-disabled="true"] .lf-node-text {
  cursor: not-allowed !important;
}

.menu-flow-designer.viewer-mode ::v-deep .lf-node[data-disabled="true"] .lf-node-text text {
  cursor: not-allowed !important;
}

.menu-flow-designer.viewer-mode ::v-deep .lf-node[data-disabled="true"] .lf-node-text div {
  cursor: not-allowed !important;
}

.menu-flow-designer.viewer-mode ::v-deep .lf-node[data-disabled="true"] foreignObject {
  cursor: not-allowed !important;
}

.menu-flow-designer.viewer-mode ::v-deep .lf-node[data-disabled="true"] foreignObject div {
  cursor: not-allowed !important;
}
</style>

<style>
.lf-control-clear {
  background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBzdGFuZGFsb25lPSJubyI/PjwhRE9DVFlQRSBzdmcgUFVCTElDICItLy9XM0MvL0RURCBTVkcgMS4xLy9FTiIgImh0dHA6Ly93d3cudzMub3JnL0dyYXBoaWNzL1NWRy8xLjEvRFREL3N2ZzExLmR0ZCI+PHN2ZyB0PSIxNjQ1Nzg5MTYyODczIiBjbGFzcz0iaWNvbiIgdmlld0JveD0iMCAwIDEwMjQgMTAyNCIgdmVyc2lvbj0iMS4xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHAtaWQ9IjIwNDYiIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCI+PGRlZnM+PHN0eWxlIHR5cGU9InRleHQvY3NzIj48L3N0eWxlPjwvZGVmcz48cGF0aCBkPSJNODk5LjEgODY5LjZsLTUzLTMwNS42SDg2NGMxNC40IDAgMjYtMTEuNiAyNi0yNlYzNDZjMC0xNC40LTExLjYtMjYtMjYtMjZINjE4VjEzOGMwLTE0LjQtMTEuNi0yNi0yNi0yNkg0MzJjLTE0LjQgMC0yNiAxMS42LTI2IDI2djE4MkgxNjBjLTE0LjQgMC0yNiAxMS42LTI2IDI2djE5MmMwIDE0LjQgMTEuNiAyNiAyNiAyNmgxNy45bC01MyAzMDUuNmMtMC4zIDEuNS0wLjQgMy0wLjQgNC40IDAgMTQuNCAxMS42IDI2IDI2IDI2aDcyM2MxLjUgMCAzLTAuMSA0LjQtMC40IDE0LjItMi40IDIzLjctMTUuOSAyMS4yLTMwek0yMDQgMzkwaDI3MlYxODJoNzJ2MjA4aDI3MnYxMDRIMjA0VjM5MHogbTQ2OCA0NDBWNjc0YzAtNC40LTMuNi04LTgtOGgtNDhjLTQuNCAwLTggMy42LTggOHYxNTZINDE2VjY3NGMwLTQuNC0zLjYtOC04LThoLTQ4Yy00LjQgMC04IDMuNi04IDh2MTU2SDIwMi44bDQ1LjEtMjYwSDc3Nmw0NS4xIDI2MEg2NzJ6IiBwLWlkPSIyMDQ3Ij48L3BhdGg+PC9zdmc+');
}

.lf-control-save {
  background-image: url('data:image/svg+xml;base64,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');
}
</style>
